import { useEffect, useRef, useState } from "react";
import useConfirm from "../hook/useConfirm";


type CustomSearchableDropdownProps = {
    label: string;
    options?: any[];
    value?: any;
    setValue?: (value: any) => void;
    selectedOptionLabel?: string;
    setSelectedOptionLabel?: (value: any) => void;
    onChange?: (value: any, label?: string) => void;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    onDemandData?: boolean;
    results?: any[];
    setResults?: (value: any[]) => void;
    fetchData?: (...value: any) => void;
    searchQuery?: string;
    setSearchQuery?: (value: any) => void;
    initialLoading?: boolean;
    loadingMore?: boolean;
    page?: number;
    setPage?: (value: number) => void;
    hasMore?: boolean;
    setHasMore?: (value: boolean) => void;
    setIsCantFindIt?: (value: boolean) => void;
    serviceType?: string;
    isAdmin?: boolean;
    inputClassNameProp?: string;
    labelClassNameProp?: string;
    isRequired?: boolean
}

export const CustomSearchableDropdown = ({
    label,
    options,
    setValue,
    selectedOptionLabel,
    setSelectedOptionLabel,
    onChange,
    placeholder,
    disabled,
    loading = false,
    onDemandData = false,
    setResults,
    results,
    fetchData,
    searchQuery,
    setSearchQuery,
    initialLoading,
    loadingMore,
    page,
    setPage,
    hasMore,
    setHasMore,
    setIsCantFindIt,
    serviceType,
    isAdmin = false,
    inputClassNameProp = "",
    labelClassNameProp = "",
    isRequired = false
}: CustomSearchableDropdownProps) => {
    const [search, setSearch] = useState<string>("");
    const [focused, setFocused] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const searchTimeoutRef = useRef<any>(null);
    const [hasInteracted, setHasInteracted] = useState(false);
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    let filteredOptions: any = [];

    const ref = useRef<HTMLDivElement | null>(null);
    const isSelectingRef = useRef(false);

    if (!onDemandData) {
        const query = search?.trim().toLowerCase();

        filteredOptions = query
            ? options && options.filter(
                opt =>
                    typeof opt?.label === "string" &&
                    opt.label.toLowerCase().includes(query)
            )
            : options;
    }



    useEffect(() => {
        if (selectedOptionLabel) {
            setSearch(selectedOptionLabel)
        }
        else {
            setSearch("")
        }
    }, [selectedOptionLabel])

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: any) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setFocused(false);
            }
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setShowDropdown(false);
                setPage && setPage(1);
                setHasMore && setHasMore(true);
                if (!searchQuery) {
                    setResults && setResults([])
                }
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetList = () => {
        setPage && setPage(1);
        setHasMore && setHasMore(true);
        setResults && setResults([])
    };

    useEffect(() => {
        if (!hasInteracted) return;
        if (isSelectingRef.current) return;

        const trimmed = (searchQuery && searchQuery.trim()) || "";
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }

        if (trimmed.length === 0) {
            resetList();
            fetchData && fetchData(0, true, "default");
            return;
        }

        if (trimmed.length < 1) {
            resetList();
            return;
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (searchQuery && searchQuery.trim().length < 1) return;

            resetList();
            fetchData && fetchData(0, true, "search");
            setShowDropdown(true);
        }, 400);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const handleInputClick = () => {
        setShowDropdown(true);
        setHasInteracted(true);

        const trimmed = searchQuery?.trim() || "";

        resetList();

        if (trimmed.length >= 1) {
            fetchData && fetchData(0, true, "search");
        } else {
            fetchData && fetchData(0, true, "default");
        }
    };


    const handleScroll = (e: any) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (
            scrollTop + clientHeight >= scrollHeight - 5 &&
            !loadingMore &&
            hasMore && fetchData && page
        ) {
            fetchData(
                page + 1 - 1,
                false,
                searchQuery && searchQuery.trim().length >= 1 ? "search" : "default"
            );
        }
    };


    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown || (results && results.length === 0)) return;

        if (e.key === "ArrowDown") {
            if (results) {
                setHighlightIndex(prev =>
                    prev + 1 >= results.length ? 0 : prev + 1
                );
            }
        }

        if (e.key === "ArrowUp") {
            if (results) {
                setHighlightIndex(prev =>
                    prev - 1 < 0 ? results.length - 1 : prev - 1
                );
            }
        }

        if (e.key === "Enter" && highlightIndex >= 0) {
            if (results) {
                handleSelect(results[highlightIndex]);
            }
        }
    };

    const handleSelect = (item: any) => {
        isSelectingRef.current = true;

        setSearchQuery && setSearchQuery(item?.label);
        setSelectedOptionLabel && setSelectedOptionLabel(item?.label);
        setValue && setValue(item?.value)
        setShowDropdown(false);
        resetList();

        setTimeout(() => {
            isSelectingRef.current = false;
        }, 0);
    };



    return (
        <div className={`${inputClassNameProp ? "flex flex-col relative " : "flex flex-col gap-2 relative"}`} ref={ref}>
            <span className={labelClassNameProp ? labelClassNameProp : "text-sm text-gray-700"}>{label}{label === "Trim (Optional)" ? "" : "*"} {label === "Vendor" && !onDemandData ? <span className="text-xs font-semibold">{`Available Vendors:${options?.length}`}</span> : ""}</span>

            <div
                className={`${inputClassNameProp ? inputClassNameProp : "border border-gray-300 rounded-xl p-[5px] text-sm bg-white"}`}
            >
                {
                    !onDemandData ? (
                        <>
                            <div className="flex items-center">
                                <input
                                    type="text"
                                    disabled={disabled}
                                    placeholder={placeholder}
                                    className={inputClassNameProp ? "outline-none w-full text-sm" : "w-full outline-none p-2"}
                                    value={search}
                                    required={isRequired}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setSelectedOptionLabel && setSelectedOptionLabel(e.target.value);
                                        setFocused(true);
                                    }}
                                    onFocus={() => !disabled && setFocused(true)}

                                />
                                {
                                    search.trim().length > 0 && (
                                        <button
                                            onClick={() => {
                                                setSearch("")
                                                setValue && setValue(0);
                                                setSelectedOptionLabel && setSelectedOptionLabel("");
                                            }}
                                            className="text-black mx-2"
                                        >
                                            <span className={`material-symbols-outlined ${inputClassNameProp ? "text-sm" : "text-lg"}`}>
                                                close
                                            </span>
                                        </button>
                                    )
                                }
                            </div>

                            {focused && !disabled && (
                                <ul className="absolute z-20 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-52 overflow-y-auto text-sm top-[70px]">
                                    {loading && (
                                        <li className="p-3 text-center text-gray-500">
                                            <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full mx-auto" />
                                        </li>
                                    )}

                                    {!loading && filteredOptions && filteredOptions.length > 0 && filteredOptions.map((opt: any, index: number) => (
                                        <li
                                            key={index}
                                            className="p-3 cursor-pointer hover:bg-gray-100 border-b"
                                            onClick={() => {
                                                if (label === "Year")
                                                    onChange && onChange(opt?.label)
                                                else
                                                    onChange && onChange(opt?.value, opt?.label)

                                                setSearch(opt?.label);
                                                setFocused(false);
                                            }}
                                        >
                                            {opt?.label}
                                        </li>
                                    ))}

                                    {
                                        (label === "Make" && (serviceType === "Tire" || serviceType === "Wheel" || !isAdmin) && options && options?.length > 0) && (
                                            <li
                                                className="p-3 cursor-pointer hover:bg-gray-100 border-b"
                                                onClick={() => {
                                                    setConfirmPopup(true);
                                                    setConfirmData({
                                                        title: "That's ok you can still book an appointement with tire size.",
                                                        confirmBtn: "Ok",
                                                        isConfirm: (callback: any) => {
                                                            setIsCantFindIt && setIsCantFindIt(true)
                                                            callback();


                                                        }
                                                    })
                                                }}
                                            >
                                                I can't find it
                                            </li>
                                        )
                                    }
                                </ul>
                            )}
                        </>
                    )
                        :
                        (

                            <>
                                <div className="flex items-center">
                                    <input
                                        type="text"
                                        disabled={disabled}
                                        placeholder={placeholder}
                                        required={isRequired}
                                        className={inputClassNameProp ? "outline-none w-full text-sm" : "w-full outline-none p-2"}
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery && setSearchQuery(e.target.value);
                                            setSelectedOptionLabel && setSelectedOptionLabel(e.target.value);
                                            if (e.target.value)
                                                setHighlightIndex(-1);

                                        }}

                                        onClick={handleInputClick}
                                        onKeyDown={handleKeyDown}
                                    />

                                    {
                                        (searchQuery && searchQuery?.trim()?.length > 0) && (
                                            <button
                                                onClick={() => {
                                                    setSearchQuery && setSearchQuery("")
                                                    setValue && setValue(0);
                                                    setSelectedOptionLabel && setSelectedOptionLabel("");
                                                }}
                                                className="text-black mx-2"
                                            >
                                                <span className={`material-symbols-outlined ${inputClassNameProp ? "text-sm" : "text-lg"}`}>
                                                    close
                                                </span>
                                            </button>
                                        )
                                    }
                                </div>

                                {showDropdown && (
                                    <ul
                                        className="absolute z-20 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-52 overflow-y-auto text-sm top-[70px]"
                                        onScroll={handleScroll}
                                    >
                                        {initialLoading ? (
                                            <li className="p-3 cursor-pointer hover:bg-gray-100 border-b">
                                                Loading...
                                            </li>
                                        ) : results && results.length > 0 ? (
                                            <>
                                                {results.map((item: any, index: number) => (
                                                    <li
                                                        key={index}
                                                        onClick={() => handleSelect(item)}
                                                        className={`p-3 cursor-pointer hover:bg-gray-100 border-b ${highlightIndex === index ? "bg-primary/20" : ""
                                                            }`}
                                                    >
                                                        {item?.label}
                                                    </li>
                                                ))}

                                                {
                                                    (label === "Model" && (serviceType === "Tire" || serviceType === "Wheel" || !!isAdmin)) && (
                                                        <li
                                                            onClick={() => {
                                                                setConfirmPopup(true);
                                                                setConfirmData({
                                                                    title: "That's ok you can still book an appointement with tire size.",
                                                                    confirmBtn: "Ok",
                                                                    isConfirm: (callback: any) => {
                                                                        setIsCantFindIt && setIsCantFindIt(true)
                                                                        callback();
                                                                    }
                                                                })
                                                            }}
                                                            className={`p-3 cursor-pointer hover:bg-gray-100 border-b`}
                                                        >
                                                            I can't find it
                                                        </li>
                                                    )
                                                }

                                                {loadingMore && (
                                                    <li className="p-3 cursor-pointer hover:bg-gray-100 border-b">
                                                        Loading more...
                                                    </li>
                                                )}
                                            </>
                                        ) : (
                                            <li className="p-3 cursor-pointer hover:bg-gray-100 border-b">
                                                No data found
                                            </li>
                                        )}
                                    </ul>
                                )}

                            </>
                        )

                }

            </div>


        </div>
    );
}